import { Confirmation } from "@app/models/Confirmation";
import { ConvenienceFeeResponse } from "@app/models/ConvenienceFeeResponse";
import { PaymentRequest, PaymentsRequestPostData } from "@app/models/PaymentRequest";
import { PaymentTimingType } from '@app/services/shared/PaymentTimingType.enum';
import { Injectable } from "@angular/core";
import { HOAApiBase } from "@app/services/apiServices/utils/HOAApiBase";
import { Observable, of } from "rxjs";
import { HttpResponse } from "@angular/common/http";
import { AdminFeeResponse } from "@app/models/AccountBalanceAdminResponse";

@Injectable({ providedIn: "root" })
export class PaymentApi extends HOAApiBase {

    constructor() {
        super('PaymentApi');
    }

    /**
     * Retrieve the convenience fee for a property
     * In the mobile and web app, the user can modify the payment amount. Convenience fees may be percentage-based, so we need to recalculate the convenience fee
     * https://webitg4.mutualofomaha.com/hoa-gateway/swagger-ui.html#!/Payment/convenienceFeePropertyIdAmountGetUsingGET
     */
    public convenienceFeePropertyIdAmountGet(
        anon: boolean,
        propertyState: string,
        billingState: string,
        amount: number,
        pmcCouponId: string,
        hoaCouponId: string,
        paymentType: "ACH" | "CARD",
        paymentTimingType: PaymentTimingType,
        scheduleFrequency: string
    ): Observable<ConvenienceFeeResponse> {
        this.check({ propertyState, billingState, pmcCouponId, hoaCouponId, paymentType }, 'is non-empty string', s => typeof s === 'string' && s.length > 0)
        this.check({ amount }, 'is number >= 0', n => Number(n) >= 0)

        if (anon) {
            return this.http.get<ConvenienceFeeResponse>(this.createUrl('HOAPass', '/guestPayment/convenienceFee'), {
                params: {
                    'propertyState': propertyState,
                    'billingState': billingState,
                    'amount': String(amount),
                    'paymentType': paymentType,
                    'pmcCouponId': pmcCouponId,
                    'hoaCouponId': hoaCouponId,
                    'paymentTimingType': paymentTimingType,
                    'scheduleFrequencyType': scheduleFrequency
                }
            })
        } else {
            return this.http.get<ConvenienceFeeResponse>(this.createUrl('hoa-gateway', '/convenienceFee'), {
                params: {
                    'propertyState': propertyState,
                    'billingState': billingState,
                    'amount': String(amount),
                    'paymentType': paymentType,
                    'pmcCouponId': pmcCouponId,
                    'hoaCouponId': hoaCouponId,
                    'paymentTimingType': paymentTimingType,
                    'scheduleFrequencyType': scheduleFrequency
                }
            })
        }
    }

    /** * Retrieves the account balance administration fee based on the provided parameters.
     **  @param {string} pmcCouponId - The PMC coupon ID used for the payment.
     ** @param {string} hoaCouponId - The HOA coupon ID used for the payment.
     ** @param {"ACH" | "CARD"} paymentType - The type of payment being processed. Accepts either "ACH" or "CARD".
     ** @param {PaymentTimingType} paymentTimingType - The timing type for the payment. Determines if the payment is immediate or scheduled.
     ** @returns {Observable<AdminFeeResponse>} An observable that emits the account balance administration response if the paymentTimingType is `SCHEDULED`.
     ** Returns `null` if the paymentTimingType is not `SCHEDULED`.
     **/

    public getAdminFee(
        pmcCouponId: string,
        hoaCouponId: string,
        paymentType: "ACH" | "CARD",
        paymentTimingType: PaymentTimingType,
        scheduleFrequency: string
    ): Observable<AdminFeeResponse> {

        return this.http.get<AdminFeeResponse>(this.createUrl('hoa-gateway', '/convenienceFee/active'), {
            params: {
                'paymentType': paymentType,
                'pmcCouponId': pmcCouponId,
                'hoaCouponId': hoaCouponId,
                'paymentTimingType': paymentTimingType,
                'scheduleFrequencyType': scheduleFrequency
            }
        });
    }
    /**
     * Update Scheduled Payment instance
     * Allows the user to submit a payment against a property with a chosen payment method.
     * @param id unique identifer for the payment instance
     */
    public paymentsIdPost(id: number): Observable<Confirmation> {
        this.check({ id }, 'is number > 0', i => Number(i) > 0)

        // I don't understand what this does
        return this.http.post<Confirmation>(this.createUrl('hoa-gateway', `/payments/${id}`), null);
    }

    /**
     * Make a Payment
     * Allows the user to submit a payment against a property with a chosen payment method.\n
     * @param anon Anonymous flag
     * @param paymentRequest Information includes property, payment method and date*/
    public paymentsPost(anon: boolean, paymentRequest: PaymentRequest): Observable<Confirmation> {
        this.check({ paymentRequest }, 'is present', p => typeof p === 'object' && p != null)

        if (anon) {
            const paymentData = mapGuestPaymentsPostData(paymentRequest);
            this.check({ paymentData }, 'converted sucessfully', p => typeof p === 'object' && p != null)
            return this.http.post<Confirmation>(this.createUrl('HOAPass', '/guestPayment/_guestPayment'), paymentData)
        } else {
            // registered
            const { futureDatedPayment } = paymentRequest
            this.check({ futureDatedPayment }, 'may not be future dated', fdp => !fdp)
            // if failure here, use the paymentsFDPPost call

            const paymentData = mapPaymentsPostData(paymentRequest);
            this.check({ paymentData }, 'converted sucessfully', p => typeof p === 'object' && p != null)
            return this.http.post<Confirmation>(this.createUrl('hoa-gateway', '/payments'), paymentData)
        }
    }


    public deletePayment(anon: boolean, paymentId: number): Observable<HttpResponse<Confirmation>> {
        this.check({ paymentId }, 'is number', p => typeof p === 'number')

        if (anon) {
            return this.http.delete<Confirmation>(this.createUrl('HOAPass', '/guestPayment/delete'), {
                observe: 'response',
                params: { paymentId }
            })
        } else {
            return this.http.delete<Confirmation>(this.createUrl('hoa-gateway', `/payments/${paymentId}`), {
                observe: 'response'
            });
        }
    }

    getPaymentDate(delayPayment: boolean, isGuest: boolean): Observable<{paymentDate: string}> {
        if (isGuest) {
            return this.http.get<{paymentDate: string}>(this.createUrl(this.getLocalPath(), '/guestPayment/payment-date'),
                {params: { delayPayment: delayPayment }}
            );
        }
        return this.http.get<{paymentDate: string}>(this.createUrl(this.getBasePath(), '/payments/payment-date'),
            {params: { delayPayment: delayPayment }}
        );
    }
}

function mapGuestPaymentsPostData(paymentRequest: PaymentRequest): any {
    try {
        // swagger formatted https://webitg4.mutualofomaha.com/hoa-gateway/swagger-ui.html#!/Payment/guestPaymentsPostUsingPOST
        return {
            'feeAmount': paymentRequest.payment.feeAmount,
            'payerId': paymentRequest.paymentMethod.payerId,
            'paymentDay': paymentRequest.paymentDate.day,
            'paymentMonth': paymentRequest.paymentDate.month,
            'paymentSiteAbbreviation': paymentRequest.paymentSiteId,
            'paymentToken': paymentRequest.paymentMethod.token,
            'paymentType': paymentRequest.paymentMethod.routingNumber ? "ACH" : "CARD",

            'cardBrandType': paymentRequest.paymentMethod.routingNumber ? null : (paymentRequest.paymentMethod.cardBrand || "DISCOVER").toUpperCase(),

            'paymentYear': paymentRequest.paymentDate.year,
            'propertyId': paymentRequest.property.id,
            'principalAmount': paymentRequest.payment.principalAmount,
            'totalPaymentAmount': paymentRequest.payment.totalPaymentAmount,
            'maskedAccountId': paymentRequest.paymentMethod.maskedId,
            'paymentMethodId': paymentRequest.paymentMethod.id,
            'acceptedAmounts': paymentRequest.acceptedTerms,
            'acceptedAmountsDate': paymentRequest.acceptedTermsDate,
            'firstName': paymentRequest.firstName,
            'lastName': paymentRequest.lastName,
            'email': paymentRequest.email,

            'futureDatedPayment': paymentRequest.futureDatedPayment,
            'effectiveDate': paymentRequest.effectiveDate,

            "paymentEntityType": paymentRequest.paymentEntityType,
            "details": paymentRequest.details,
            "correlationId": paymentRequest.correlationId,
            "duplicateCheck": paymentRequest.duplicateCheck
        }
    } catch (_err) {
        return null
    }
}

function mapPaymentsPostData(paymentRequest: PaymentRequest): PaymentsRequestPostData {
    try {
        // swagger formatted https://webitg4.mutualofomaha.com/hoa-gateway/swagger-ui.html#/Payment/paymentsPostUsingPOST
        return {
            'feeAmount': paymentRequest.payment.feeAmount,
            'payerId': paymentRequest.paymentMethod.payerId,
            'paymentDay': paymentRequest.paymentDate.day,
            'paymentMonth': paymentRequest.paymentDate.month,
            'paymentSiteAbbreviation': paymentRequest.paymentSiteId,
            'paymentToken': paymentRequest.paymentMethod.token,
            'paymentType': paymentRequest.paymentMethod.routingNumber ? "ACH" : "CARD",
            'paymentYear': paymentRequest.paymentDate.year,
            'propertyId': paymentRequest.property.id,
            'principalAmount': paymentRequest.payment.principalAmount,
            'totalPaymentAmount': paymentRequest.payment.totalPaymentAmount,
            'maskedAccountId': paymentRequest.paymentMethod.maskedId,
            'paymentChannel': paymentRequest.paymentChannel,
            'paymentMethodId': paymentRequest.paymentMethod.id,
            'acceptedAmounts': paymentRequest.acceptedTerms,
            'acceptedAmountsDate': paymentRequest.acceptedTermsDate,
            'paymentEntityType': paymentRequest.paymentEntityType,
            'details': paymentRequest.details,
            'correlationId': paymentRequest.correlationId,
            'duplicateCheck': paymentRequest.duplicateCheck
        }
    } catch (_err) {
        return null
    }
}

